import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { CRMImportDialog } from '@/components/CRMImportDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Helmet } from 'react-helmet-async';
import {
  Users, Search, Download, Tag, TrendingUp, UserPlus,
  Activity, Loader2, Edit2, Trash2, MoreHorizontal, Calendar, Eye,
  Kanban, ListTodo, Plus, CheckCircle2, Clock, AlertCircle, Phone, Mail, StickyNote,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';

interface Contact {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  source: string | null;
  tags: string[] | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  submissionCount?: number;
}

interface Deal {
  id: string;
  contact_id: string | null;
  title: string;
  stage: 'new' | 'contacted' | 'qualified' | 'opportunity' | 'won' | 'lost';
  value: number | null;
  currency: string;
  expected_close_date: string | null;
  notes: string | null;
  created_at: string;
}

interface CRMActivity {
  id: string;
  contact_id: string | null;
  deal_id: string | null;
  type: 'call' | 'email' | 'note' | 'task' | 'meeting';
  title: string;
  description: string | null;
  due_at: string | null;
  completed_at: string | null;
  created_at: string;
}

const STAGES: { key: Deal['stage']; label: string; color: string }[] = [
  { key: 'new', label: 'New', color: 'bg-slate-500' },
  { key: 'contacted', label: 'Contacted', color: 'bg-blue-500' },
  { key: 'qualified', label: 'Qualified', color: 'bg-amber-500' },
  { key: 'opportunity', label: 'Opportunity', color: 'bg-purple-500' },
  { key: 'won', label: 'Won', color: 'bg-emerald-500' },
  { key: 'lost', label: 'Lost', color: 'bg-red-500' },
];

const ACTIVITY_ICONS = {
  call: Phone, email: Mail, note: StickyNote, task: ListTodo, meeting: Calendar,
} as const;

const CRM = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [activities, setActivities] = useState<CRMActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [tagFilter, setTagFilter] = useState<string | null>(null);

  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', notes: '', tags: '' });

  const [isAddDealOpen, setIsAddDealOpen] = useState(false);
  const [dealForm, setDealForm] = useState({ title: '', contact_id: '', stage: 'new' as Deal['stage'], value: '', notes: '' });

  const [isAddActivityOpen, setIsAddActivityOpen] = useState(false);
  const [activityForm, setActivityForm] = useState({ type: 'note' as CRMActivity['type'], title: '', description: '', contact_id: '', due_at: '' });

  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [bulkTagValue, setBulkTagValue] = useState('');

  useEffect(() => { if (user) fetchAll(); }, [user]);

  const fetchAll = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const [{ data: contactsData }, { data: dealsData }, { data: activitiesData }, { data: submissions }] = await Promise.all([
        supabase.from('contacts').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('crm_deals' as any).select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('crm_activities' as any).select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('submissions').select('id, data, forms!inner(user_id)').eq('forms.user_id', user.id),
      ]);

      const contactsList: Contact[] = (contactsData || []).map((c: any) => {
        const count = (submissions || []).filter((s: any) => {
          const d = s.data as any;
          return d?.email === c.email || d?.phone === c.phone;
        }).length;
        return { ...c, submissionCount: count };
      });

      setContacts(contactsList);
      setDeals((dealsData as unknown as Deal[]) || []);
      setActivities((activitiesData as unknown as CRMActivity[]) || []);
    } catch (e: any) {
      console.error(e);
      toast({ title: 'Error loading CRM', description: e.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredContacts = contacts.filter(c => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = !q || c.name.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.phone?.includes(q);
    const matchesTag = !tagFilter || (c.tags || []).includes(tagFilter);
    return matchesSearch && matchesTag;
  });

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredContacts.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredContacts.map(c => c.id)));
  };

  // Contact CRUD
  const handleAddContact = async () => {
    if (!user || !editForm.name) return toast({ title: 'Name is required', variant: 'destructive' });
    const tagsArr = editForm.tags.split(',').map(t => t.trim()).filter(Boolean);
    const { error } = await supabase.from('contacts').insert({
      user_id: user.id,
      name: editForm.name,
      email: editForm.email || null,
      phone: editForm.phone || null,
      notes: editForm.notes || null,
      tags: tagsArr.length ? tagsArr : null,
      source: 'manual',
    });
    if (error) return toast({ title: 'Error', description: error.message, variant: 'destructive' });
    toast({ title: 'Contact added' });
    setIsAddContactOpen(false);
    setEditForm({ name: '', email: '', phone: '', notes: '', tags: '' });
    fetchAll();
  };

  const handleUpdateContact = async () => {
    if (!selectedContact) return;
    const tagsArr = editForm.tags.split(',').map(t => t.trim()).filter(Boolean);
    const { error } = await supabase.from('contacts').update({
      name: editForm.name,
      email: editForm.email || null,
      phone: editForm.phone || null,
      notes: editForm.notes || null,
      tags: tagsArr.length ? tagsArr : null,
    }).eq('id', selectedContact.id);
    if (error) return toast({ title: 'Error', description: error.message, variant: 'destructive' });
    toast({ title: 'Contact updated' });
    setIsDetailOpen(false);
    fetchAll();
  };

  const handleDeleteContact = async (id: string) => {
    const { error } = await supabase.from('contacts').delete().eq('id', id);
    if (error) return toast({ title: 'Error', description: error.message, variant: 'destructive' });
    toast({ title: 'Contact deleted' });
    setIsDetailOpen(false);
    fetchAll();
  };

  const openDetail = (c: Contact) => {
    setSelectedContact(c);
    setEditForm({
      name: c.name, email: c.email || '', phone: c.phone || '',
      notes: c.notes || '', tags: (c.tags || []).join(', '),
    });
    setIsDetailOpen(true);
  };

  // Bulk actions
  const handleBulkDelete = async () => {
    const { error } = await supabase.from('contacts').delete().in('id', Array.from(selectedIds));
    if (error) return toast({ title: 'Error', description: error.message, variant: 'destructive' });
    toast({ title: `${selectedIds.size} deleted` });
    setSelectedIds(new Set());
    fetchAll();
  };

  const handleBulkTag = async () => {
    if (!bulkTagValue.trim()) return;
    const newTag = bulkTagValue.trim();
    const updates = Array.from(selectedIds).map(id => {
      const c = contacts.find(x => x.id === id);
      const tags = Array.from(new Set([...(c?.tags || []), newTag]));
      return supabase.from('contacts').update({ tags }).eq('id', id);
    });
    await Promise.all(updates);
    toast({ title: `Tagged ${selectedIds.size} contacts with "${newTag}"` });
    setBulkTagValue('');
    setBulkTagOpen(false);
    setSelectedIds(new Set());
    fetchAll();
  };

  const exportCSV = (rows: Contact[]) => {
    const csv = [
      ['Name', 'Email', 'Phone', 'Tags', 'Source', 'Created'],
      ...rows.map(c => [
        c.name, c.email || '', c.phone || '',
        (c.tags || []).join(';'), c.source || '',
        new Date(c.created_at).toLocaleDateString(),
      ]),
    ].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contacts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Exported', description: `${rows.length} contacts` });
  };

  // Deal CRUD
  const handleAddDeal = async () => {
    if (!user || !dealForm.title) return toast({ title: 'Title required', variant: 'destructive' });
    const { error } = await supabase.from('crm_deals' as any).insert({
      user_id: user.id,
      title: dealForm.title,
      contact_id: dealForm.contact_id || null,
      stage: dealForm.stage,
      value: dealForm.value ? Number(dealForm.value) : 0,
      notes: dealForm.notes || null,
    });
    if (error) return toast({ title: 'Error', description: error.message, variant: 'destructive' });
    toast({ title: 'Deal created' });
    setIsAddDealOpen(false);
    setDealForm({ title: '', contact_id: '', stage: 'new', value: '', notes: '' });
    fetchAll();
  };

  const updateDealStage = async (dealId: string, stage: Deal['stage']) => {
    const { error } = await supabase.from('crm_deals' as any).update({ stage }).eq('id', dealId);
    if (error) return toast({ title: 'Error', description: error.message, variant: 'destructive' });
    setDeals(prev => prev.map(d => d.id === dealId ? { ...d, stage } : d));
  };

  const deleteDeal = async (dealId: string) => {
    const { error } = await supabase.from('crm_deals' as any).delete().eq('id', dealId);
    if (error) return toast({ title: 'Error', description: error.message, variant: 'destructive' });
    toast({ title: 'Deal removed' });
    fetchAll();
  };

  // Activities
  const handleAddActivity = async () => {
    if (!user || !activityForm.title) return toast({ title: 'Title required', variant: 'destructive' });
    const { error } = await supabase.from('crm_activities' as any).insert({
      user_id: user.id,
      type: activityForm.type,
      title: activityForm.title,
      description: activityForm.description || null,
      contact_id: activityForm.contact_id || null,
      due_at: activityForm.due_at || null,
    });
    if (error) return toast({ title: 'Error', description: error.message, variant: 'destructive' });
    toast({ title: 'Activity logged' });
    setIsAddActivityOpen(false);
    setActivityForm({ type: 'note', title: '', description: '', contact_id: '', due_at: '' });
    fetchAll();
  };

  const toggleActivityComplete = async (a: CRMActivity) => {
    const completed_at = a.completed_at ? null : new Date().toISOString();
    const { error } = await supabase.from('crm_activities' as any).update({ completed_at }).eq('id', a.id);
    if (error) return toast({ title: 'Error', description: error.message, variant: 'destructive' });
    setActivities(prev => prev.map(x => x.id === a.id ? { ...x, completed_at } : x));
  };

  const deleteActivity = async (id: string) => {
    const { error } = await supabase.from('crm_activities' as any).delete().eq('id', id);
    if (error) return toast({ title: 'Error', description: error.message, variant: 'destructive' });
    fetchAll();
  };

  // Derived
  const allTags = Array.from(new Set(contacts.flatMap(c => c.tags || []))).sort();
  const tasksDueToday = activities.filter(a => a.type === 'task' && !a.completed_at && a.due_at && new Date(a.due_at) <= new Date(new Date().setHours(23, 59, 59, 999))).length;
  const openDeals = deals.filter(d => d.stage !== 'won' && d.stage !== 'lost').length;

  const statCards = [
    { label: 'Total Contacts', value: contacts.length, icon: Users, color: 'text-primary' },
    { label: 'Open Deals', value: openDeals, icon: Kanban, color: 'text-purple-500' },
    { label: 'Tasks Due Today', value: tasksDueToday, icon: ListTodo, color: 'text-orange-500' },
    { label: 'Tags', value: allTags.length, icon: Tag, color: 'text-blue-500' },
  ];

  const isPaidPlan = user && ['starter', 'pro', 'business', 'enterprise'].includes((user as any).plan?.toLowerCase() || '');
  if (!isPaidPlan) {
    return (
      <DashboardLayout>
        <div className="flex-1 flex items-center justify-center p-6">
          <Card className="max-w-md w-full">
            <CardContent className="p-12 text-center">
              <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">CRM</h2>
              <p className="text-muted-foreground mb-6">
                Upgrade to a paid plan to access the full CRM with pipeline, activities, and tags.
              </p>
              <Button onClick={() => window.location.href = '/billing-new'}>Upgrade Now</Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Helmet><title>CRM | Bravax Forms</title></Helmet>
      <div className="p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">CRM Workspace</h1>
            <p className="text-sm text-muted-foreground">Contacts, pipeline, activities, and tags</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <CRMImportDialog onImportComplete={fetchAll} />
            <Button size="sm" variant="outline" onClick={() => exportCSV(filteredContacts)}>
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
            <Button size="sm" onClick={() => setIsAddContactOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" /> Add Contact
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((s, i) => (
            <Card key={i} className="border border-border/50">
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center ${s.color}`}>
                  <s.icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{s.value}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="contacts" className="space-y-6">
          <TabsList>
            <TabsTrigger value="contacts"><Users className="h-4 w-4 mr-1.5" />Contacts</TabsTrigger>
            <TabsTrigger value="pipeline"><Kanban className="h-4 w-4 mr-1.5" />Pipeline</TabsTrigger>
            <TabsTrigger value="activities"><Activity className="h-4 w-4 mr-1.5" />Activities</TabsTrigger>
            <TabsTrigger value="tags"><Tag className="h-4 w-4 mr-1.5" />Tags</TabsTrigger>
          </TabsList>

          {/* CONTACTS TAB */}
          <TabsContent value="contacts" className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
              <div className="relative flex-1 w-full sm:max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search contacts..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
              </div>
              {tagFilter && (
                <Badge variant="secondary" className="gap-1">
                  Tag: {tagFilter}
                  <button onClick={() => setTagFilter(null)} className="ml-1 hover:text-destructive">×</button>
                </Badge>
              )}
              {selectedIds.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{selectedIds.size} selected</span>
                  <Button size="sm" variant="outline" onClick={() => setBulkTagOpen(true)}>
                    <Tag className="mr-1 h-3.5 w-3.5" /> Tag
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => exportCSV(contacts.filter(c => selectedIds.has(c.id)))}>
                    <Download className="mr-1 h-3.5 w-3.5" /> Export
                  </Button>
                  <Button size="sm" variant="destructive" onClick={handleBulkDelete}>
                    <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                  </Button>
                </div>
              )}
            </div>

            <Card className="border border-border/50 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedIds.size === filteredContacts.length && filteredContacts.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="hidden sm:table-cell">Email</TableHead>
                      <TableHead className="hidden md:table-cell">Phone</TableHead>
                      <TableHead className="hidden lg:table-cell">Tags</TableHead>
                      <TableHead className="hidden md:table-cell">Added</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredContacts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12">
                          <Users className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
                          <p className="text-sm text-muted-foreground">
                            {searchTerm || tagFilter ? 'No contacts match your filters' : 'No contacts yet.'}
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : filteredContacts.map(c => (
                      <TableRow key={c.id} className="hover:bg-muted/20 cursor-pointer" onClick={() => openDetail(c)}>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={selectedIds.has(c.id)} onCheckedChange={() => toggleSelect(c.id)} />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                                {c.name.split(' ').map(n => n[0] || '').join('').slice(0, 2).toUpperCase() || 'U'}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium">{c.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell text-muted-foreground text-sm">{c.email || '—'}</TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground text-sm">{c.phone || '—'}</TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {(c.tags || []).slice(0, 3).map(t => (
                              <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                          {new Date(c.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openDetail(c)}>
                                <Eye className="mr-2 h-3.5 w-3.5" /> View
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openDetail(c)}>
                                <Edit2 className="mr-2 h-3.5 w-3.5" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteContact(c.id)}>
                                <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* PIPELINE TAB */}
          <TabsContent value="pipeline" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{deals.length} total deals</p>
              <Button size="sm" onClick={() => setIsAddDealOpen(true)}>
                <Plus className="mr-2 h-4 w-4" /> Add Deal
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {STAGES.map(s => {
                const stageDeals = deals.filter(d => d.stage === s.key);
                const total = stageDeals.reduce((sum, d) => sum + (Number(d.value) || 0), 0);
                return (
                  <Card key={s.key} className="border border-border/50">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`h-2 w-2 rounded-full ${s.color}`} />
                          <CardTitle className="text-sm font-semibold">{s.label}</CardTitle>
                        </div>
                        <Badge variant="secondary" className="text-xs">{stageDeals.length}</Badge>
                      </div>
                      {total > 0 && <p className="text-xs text-muted-foreground mt-1">${total.toLocaleString()}</p>}
                    </CardHeader>
                    <CardContent className="space-y-2 min-h-[100px]">
                      {stageDeals.map(d => {
                        const contact = contacts.find(c => c.id === d.contact_id);
                        return (
                          <div key={d.id} className="p-3 bg-muted/30 rounded-lg border border-border/40 hover:border-primary/50 transition-colors">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <p className="text-sm font-medium leading-snug">{d.title}</p>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-6 w-6 -mr-1 -mt-1">
                                    <MoreHorizontal className="h-3 w-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {STAGES.filter(x => x.key !== d.stage).map(x => (
                                    <DropdownMenuItem key={x.key} onClick={() => updateDealStage(d.id, x.key)}>
                                      Move to {x.label}
                                    </DropdownMenuItem>
                                  ))}
                                  <DropdownMenuItem className="text-destructive" onClick={() => deleteDeal(d.id)}>
                                    <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                            {contact && <p className="text-xs text-muted-foreground">{contact.name}</p>}
                            {d.value ? <p className="text-xs font-semibold text-foreground mt-1">${Number(d.value).toLocaleString()}</p> : null}
                          </div>
                        );
                      })}
                      {stageDeals.length === 0 && (
                        <p className="text-xs text-muted-foreground/60 text-center py-4">No deals</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* ACTIVITIES TAB */}
          <TabsContent value="activities" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{activities.length} activities</p>
              <Button size="sm" onClick={() => setIsAddActivityOpen(true)}>
                <Plus className="mr-2 h-4 w-4" /> Log Activity
              </Button>
            </div>
            <Card className="border border-border/50">
              <CardContent className="p-0">
                {activities.length === 0 ? (
                  <div className="p-12 text-center">
                    <Activity className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                    <p className="text-sm text-muted-foreground">No activities logged yet.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border/40">
                    {activities.map(a => {
                      const Icon = ACTIVITY_ICONS[a.type];
                      const contact = contacts.find(c => c.id === a.contact_id);
                      const overdue = a.type === 'task' && !a.completed_at && a.due_at && new Date(a.due_at) < new Date();
                      return (
                        <div key={a.id} className="flex items-start gap-4 p-4 hover:bg-muted/20">
                          {a.type === 'task' ? (
                            <button onClick={() => toggleActivityComplete(a)} className="mt-0.5">
                              {a.completed_at ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Clock className="h-5 w-5 text-muted-foreground" />}
                            </button>
                          ) : (
                            <div className="mt-0.5"><Icon className="h-5 w-5 text-primary" /></div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className={`text-sm font-medium ${a.completed_at ? 'line-through text-muted-foreground' : ''}`}>{a.title}</p>
                              <Badge variant="outline" className="text-[10px] capitalize">{a.type}</Badge>
                              {overdue && <Badge variant="destructive" className="text-[10px] gap-1"><AlertCircle className="h-3 w-3" />Overdue</Badge>}
                            </div>
                            {a.description && <p className="text-xs text-muted-foreground mt-1">{a.description}</p>}
                            <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                              {contact && <span>{contact.name}</span>}
                              {a.due_at && <span>Due {new Date(a.due_at).toLocaleString()}</span>}
                              <span>{new Date(a.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deleteActivity(a.id)}>
                            <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAGS TAB */}
          <TabsContent value="tags" className="space-y-4">
            <Card className="border border-border/50">
              <CardContent className="p-6">
                {allTags.length === 0 ? (
                  <div className="text-center py-8">
                    <Tag className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                    <p className="text-sm text-muted-foreground">No tags yet. Add tags to contacts to organize them.</p>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {allTags.map(tag => {
                      const count = contacts.filter(c => (c.tags || []).includes(tag)).length;
                      return (
                        <button
                          key={tag}
                          onClick={() => { setTagFilter(tag); const el = document.querySelector('[value="contacts"]') as HTMLElement; el?.click(); }}
                          className="group flex items-center gap-2 px-3 py-2 bg-muted/40 hover:bg-primary/10 rounded-lg transition-colors"
                        >
                          <Tag className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary" />
                          <span className="text-sm font-medium">{tag}</span>
                          <Badge variant="secondary" className="text-xs">{count}</Badge>
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Contact Detail Dialog */}
        <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Contact Details</DialogTitle></DialogHeader>
            {selectedContact && (
              <div className="space-y-4">
                <div className="grid gap-3">
                  <div><Label>Name</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Email</Label><Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="mt-1" /></div>
                    <div><Label>Phone</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="mt-1" /></div>
                  </div>
                  <div><Label>Tags (comma-separated)</Label><Input value={editForm.tags} onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })} className="mt-1" placeholder="vip, cold-lead" /></div>
                  <div><Label>Notes</Label><Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="mt-1" rows={3} /></div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Recent Activity</Label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                    {activities.filter(a => a.contact_id === selectedContact.id).slice(0, 5).map(a => {
                      const Icon = ACTIVITY_ICONS[a.type];
                      return (
                        <div key={a.id} className="flex items-center gap-2 text-xs p-2 bg-muted/30 rounded">
                          <Icon className="h-3.5 w-3.5 text-primary" />
                          <span className="flex-1">{a.title}</span>
                          <span className="text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</span>
                        </div>
                      );
                    })}
                    {activities.filter(a => a.contact_id === selectedContact.id).length === 0 && (
                      <p className="text-xs text-muted-foreground italic">No activity yet.</p>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => handleDeleteContact(selectedContact.id)} className="text-destructive"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
                  <Button onClick={handleUpdateContact}>Save Changes</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Contact Dialog */}
        <Dialog open={isAddContactOpen} onOpenChange={setIsAddContactOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Contact</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name *</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="mt-1" /></div>
                <div><Label>Phone</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="mt-1" /></div>
              </div>
              <div><Label>Tags</Label><Input value={editForm.tags} onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })} className="mt-1" placeholder="vip, lead" /></div>
              <div><Label>Notes</Label><Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="mt-1" rows={2} /></div>
              <Button onClick={handleAddContact} className="w-full">Add Contact</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Deal Dialog */}
        <Dialog open={isAddDealOpen} onOpenChange={setIsAddDealOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>New Deal</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title *</Label><Input value={dealForm.title} onChange={(e) => setDealForm({ ...dealForm, title: e.target.value })} className="mt-1" placeholder="Deal name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Stage</Label>
                  <Select value={dealForm.stage} onValueChange={(v) => setDealForm({ ...dealForm, stage: v as Deal['stage'] })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Value ($)</Label><Input type="number" value={dealForm.value} onChange={(e) => setDealForm({ ...dealForm, value: e.target.value })} className="mt-1" /></div>
              </div>
              <div>
                <Label>Contact</Label>
                <Select value={dealForm.contact_id || 'none'} onValueChange={(v) => setDealForm({ ...dealForm, contact_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Notes</Label><Textarea value={dealForm.notes} onChange={(e) => setDealForm({ ...dealForm, notes: e.target.value })} className="mt-1" rows={2} /></div>
              <Button onClick={handleAddDeal} className="w-full">Create Deal</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Activity Dialog */}
        <Dialog open={isAddActivityOpen} onOpenChange={setIsAddActivityOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Log Activity</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Type</Label>
                <Select value={activityForm.type} onValueChange={(v) => setActivityForm({ ...activityForm, type: v as CRMActivity['type'] })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">Note</SelectItem>
                    <SelectItem value="call">Call</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="task">Task</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Title *</Label><Input value={activityForm.title} onChange={(e) => setActivityForm({ ...activityForm, title: e.target.value })} className="mt-1" /></div>
              <div><Label>Description</Label><Textarea value={activityForm.description} onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })} className="mt-1" rows={2} /></div>
              <div>
                <Label>Related Contact</Label>
                <Select value={activityForm.contact_id || 'none'} onValueChange={(v) => setActivityForm({ ...activityForm, contact_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {activityForm.type === 'task' && (
                <div><Label>Due Date</Label><Input type="datetime-local" value={activityForm.due_at} onChange={(e) => setActivityForm({ ...activityForm, due_at: e.target.value })} className="mt-1" /></div>
              )}
              <Button onClick={handleAddActivity} className="w-full">Save Activity</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Tag Dialog */}
        <Dialog open={bulkTagOpen} onOpenChange={setBulkTagOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add tag to {selectedIds.size} contact(s)</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input value={bulkTagValue} onChange={(e) => setBulkTagValue(e.target.value)} placeholder="Tag name" autoFocus />
              <Button onClick={handleBulkTag} className="w-full">Apply Tag</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CRM;
